import {
  PolicyMetricsResponse,
  PolicyBarChartResponse,
  DTBBarChartResponse,
  LexipolUpdatesResponse,
  AssignmentsResponse,
} from '../_types';
import { getMetricStatus } from '../_constants';

// Helper function to cache/retrieve data from sessionStorage
function getCachedOrGenerate<T>(key: string, generator: () => T): T {
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) {
      return JSON.parse(cached) as T;
    }
    const data = generator();
    sessionStorage.setItem(key, JSON.stringify(data));
    return data;
  } catch (error) {
    // If sessionStorage fails, just generate fresh data
    return generator();
  }
}

// Helper to generate cache key
function getCacheKey(orgId: string, startDate: string, endDate: string, endpoint: string): string {
  return `risk_pool_org_${orgId}_${startDate}_${endDate}_${endpoint}`;
}

// Helper to generate random number in range
function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// Helper to generate percentage from counts
function calculatePercent(count: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((count / total) * 100 * 10) / 10;
}

// Generate policy metrics
export function generatePolicyMetrics(
  orgId: string,
  startDate: string,
  endDate: string
): PolicyMetricsResponse {
  const cacheKey = getCacheKey(orgId, startDate, endDate, 'policy_metrics');
  
  return getCachedOrGenerate(cacheKey, () => {
    // Acknowledgments completed
    const ackTotal = randomInt(500, 1500);
    const ackCompleted = randomInt(Math.floor(ackTotal * 0.3), Math.floor(ackTotal * 0.95));
    const ackPercent = calculatePercent(ackCompleted, ackTotal);
    
    // DTBs taken
    const dtbTotal = randomInt(2000, 5000);
    const dtbCompleted = randomInt(Math.floor(dtbTotal * 0.5), Math.floor(dtbTotal * 0.9));
    const dtbPercent = calculatePercent(dtbCompleted, dtbTotal);
    
    // Lexipol updates
    const lexipolTotal = randomInt(200, 500);
    const lexipolProcessed = randomInt(Math.floor(lexipolTotal * 0.2), Math.floor(lexipolTotal * 0.8));
    const lexipolPercent = calculatePercent(lexipolProcessed, lexipolTotal);
    
    // Policies published
    const policiesTotal = randomInt(300, 450);
    const policiesPercentChange = randomInt(-10, 15);
    
    // DTBs issued
    const lexipolIssuedDTBs = randomInt(50, 150);
    const agencyIssuedDTBs = randomInt(20, Math.floor(lexipolIssuedDTBs * 0.8));
    const dtbIssuedPercent = calculatePercent(agencyIssuedDTBs, lexipolIssuedDTBs);
    
    return {
      acknowledgments_completed: {
        percent: ackPercent,
        count: ackCompleted,
        totalCount: ackTotal,
        status: getMetricStatus(ackPercent),
      },
      dtbs_taken: {
        percent: dtbPercent,
        count: dtbCompleted,
        totalCount: dtbTotal,
        status: getMetricStatus(dtbPercent),
      },
      lexipol_updates: {
        percent: lexipolPercent,
        count: lexipolProcessed,
        totalCount: lexipolTotal,
        status: getMetricStatus(lexipolPercent),
      },
      policies_published: {
        total: policiesTotal,
        percentChange: policiesPercentChange,
        status: policiesPercentChange >= 5 ? 'GOOD' : policiesPercentChange <= -5 ? 'CRITICAL' : 'CONCERNING',
      },
      dtbs_issued: {
        percent: dtbIssuedPercent,
        agencyIssuedCount: agencyIssuedDTBs,
        lexipolIssuedCount: lexipolIssuedDTBs,
        status: getMetricStatus(dtbIssuedPercent),
      },
    };
  });
}

// Helper to calculate number of periods based on date range
function calculatePeriods(startDate: string, endDate: string): { useWeeks: boolean; periods: number } {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  
  // If period is about a month (± day), use 4 weekly periods
  const useWeeks = diffDays <= 31;
  
  if (useWeeks) {
    return { useWeeks: true, periods: 4 };
  }
  
  // For more than a month, calculate number of complete months
  // Since we're using first day of month for periods, we just need the month difference + 1
  const monthsDiff = 
    (end.getFullYear() - start.getFullYear()) * 12 + 
    (end.getMonth() - start.getMonth()) + 1; // +1 to include both start and end months
  
  return { useWeeks: false, periods: Math.max(1, monthsDiff) };
}

// Generate policy trends (stacked bar chart data)
export function generatePolicyTrends(
  orgId: string,
  startDate: string,
  endDate: string
): PolicyBarChartResponse {
  const cacheKey = getCacheKey(orgId, startDate, endDate, 'policy_trends');
  
  return getCachedOrGenerate(cacheKey, () => {
    const start = new Date(startDate);
    const { useWeeks, periods } = calculatePeriods(startDate, endDate);
    const metrics: PolicyBarChartResponse['metrics'] = [];

    for (let i = 0; i < periods; i++) {
      let periodDate: Date;
      if (useWeeks) {
        periodDate = new Date(start);
        periodDate.setDate(start.getDate() + (i * 7));
      } else {
        periodDate = new Date(start.getFullYear(), start.getMonth() + i, 1);
      }

      metrics.push({
        period: periodDate.toISOString().split('T')[0],
        lexipolUpdates: {
          pendingCount: randomInt(3, 12),
          processedCount: randomInt(5, 15),
        },
        assignmentsCompleted: {
          overdueByWeekOrMore: randomInt(1, 5),
          overdueByLessThanWeek: randomInt(3, 10),
          onTime: randomInt(30, 60),
        },
        assignmentsNotCompleted: {
          overdueByWeekOrMore: randomInt(0, 4),
          overdueByLessThanWeek: randomInt(1, 6),
        },
      });
    }

    return { metrics };
  });
}

// Generate DTB trends (stacked bar chart data)
export function generateDTBTrends(
  orgId: string,
  startDate: string,
  endDate: string
): DTBBarChartResponse {
  const cacheKey = getCacheKey(orgId, startDate, endDate, 'dtb_trends');
  
  return getCachedOrGenerate(cacheKey, () => {
    const start = new Date(startDate);
    const { useWeeks, periods } = calculatePeriods(startDate, endDate);
    const metrics: DTBBarChartResponse['metrics'] = [];

    for (let i = 0; i < periods; i++) {
      let periodDate: Date;
      if (useWeeks) {
        periodDate = new Date(start);
        periodDate.setDate(start.getDate() + (i * 7));
      } else {
        periodDate = new Date(start.getFullYear(), start.getMonth() + i, 1);
      }

      metrics.push({
        period: periodDate.toISOString().split('T')[0],
        completedDTBs: {
          onTime: randomInt(20, 40),
          late: randomInt(5, 15),
        },
        assignmentsCompleted: {
          overdueByWeekOrMore: randomInt(1, 5),
          overdueByLessThanWeek: randomInt(3, 8),
          onTime: randomInt(20, 40),
        },
        assignmentsNotCompleted: {
          overdueByWeekOrMore: randomInt(0, 3),
          overdueByLessThanWeek: randomInt(1, 5),
        },
      });
    }

    return { metrics };
  });
}

// Generate Lexipol updates (pie chart data)
export function generateLexipolUpdates(
  orgId: string,
  startDate: string,
  endDate: string
): LexipolUpdatesResponse {
  const cacheKey = getCacheKey(orgId, startDate, endDate, 'lexipol_updates');
  
  return getCachedOrGenerate(cacheKey, () => {
    const accepted = randomInt(15, 40);
    const modified = randomInt(10, 30);
    const rejected = randomInt(3, 15);
    
    return {
      acceptedCount: accepted,
      modifiedCount: modified,
      rejectedCount: rejected,
    };
  });
}

// Generate assignments (line chart data)
export function generateAssignments(
  orgId: string,
  startDate: string,
  endDate: string
): AssignmentsResponse {
  const cacheKey = getCacheKey(orgId, startDate, endDate, 'assignments');
  
  return getCachedOrGenerate(cacheKey, () => {
    const start = new Date(startDate);
    const { useWeeks, periods } = calculatePeriods(startDate, endDate);
    const metrics: AssignmentsResponse['metrics'] = [];

    for (let i = 0; i < periods; i++) {
      let periodDate: Date;
      if (useWeeks) {
        periodDate = new Date(start);
        periodDate.setDate(start.getDate() + (i * 7));
      } else {
        periodDate = new Date(start.getFullYear(), start.getMonth() + i, 1);
      }

      const ackCompleted = randomInt(15, 30);
      const ackOverdue = randomInt(3, 10);
      const dtbCompleted = randomInt(5, 15);
      const dtbOverdue = randomInt(1, 5);

      metrics.push({
        period: periodDate.toISOString().split('T')[0],
        completedCount: ackCompleted + dtbCompleted,
        overdueCount: ackOverdue + dtbOverdue,
        acknowledgments: {
          completedCount: ackCompleted,
          overdueCount: ackOverdue,
        },
        dtbs: {
          completedCount: dtbCompleted,
          overdueCount: dtbOverdue,
        },
      });
    }

    return { metrics };
  });
}

